"use client";

import Script from "next/script";
import { useEffect, useRef, useState } from "react";

type PGEligibilityWidgetType = {
  initialize: (id: string, pid: string, apiKey: string, locale: string) => void;
};

type GlobalThisWithPGEligibilityWidget = typeof globalThis & {
  PGEligibilityWidget?: PGEligibilityWidgetType;
};

export type PlusgradeFormClientProperties = {
  apiKey: string;
  envScriptUrl: string;
  locale: string;
  pid: string;
  scriptUrl: string;
  shouldLoadNprdEnvScript: boolean;
};

type PlusgradeFormInitialization = {
  apiKey: string;
  id: string;
  locale: string;
  pid: string;
};

const initializePlusgradeEligibilityWidget = ({
  apiKey,
  id,
  locale,
  pid,
}: PlusgradeFormInitialization) => {
  const globalThisWithPGEligibilityWidget =
    globalThis as GlobalThisWithPGEligibilityWidget;

  globalThisWithPGEligibilityWidget.PGEligibilityWidget?.initialize(
    id,
    pid,
    apiKey,
    locale,
  );
};

export const PlusgradeFormClient = ({
  locale,
  envScriptUrl,
  shouldLoadNprdEnvScript,
  scriptUrl,
  apiKey,
  pid,
}: PlusgradeFormClientProperties) => {
  const [areEnvironmentVariablesReady, setAreEnvironmentVariablesReady] =
    useState(shouldLoadNprdEnvScript ? false : true);
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  const isWidgetLoadedReference = useRef<boolean>(false);

  useEffect(() => {
    if (!(isWidgetReady && areEnvironmentVariablesReady)) {
      return;
    }

    initializePlusgradeEligibilityWidget({
      apiKey,
      id: "pg-ew",
      locale,
      pid,
    });
    isWidgetLoadedReference.current = true;
  }, [areEnvironmentVariablesReady, isWidgetReady]);

  useEffect(() => {
    if (isWidgetLoadedReference.current) {
      return;
    }

    initializePlusgradeEligibilityWidget({
      apiKey,
      id: "pg-ew",
      locale,
      pid,
    });
    isWidgetLoadedReference.current = true;
  }, [locale]);

  return (
    <>
      {envScriptUrl && shouldLoadNprdEnvScript && (
        <Script
          async
          id="__pg_eligibility_widget_env__"
          onLoad={() => {
            setAreEnvironmentVariablesReady(true);
          }}
          src={envScriptUrl}
        />
      )}
      <Script
        async
        id="__pg_eligibility_widget__"
        onLoad={() => {
          setIsWidgetReady(true);
        }}
        src={scriptUrl}
      />
      <div
        className="
          container mx-auto min-h-111 text-start

          md:min-h-84

          lg:min-h-78.5

          [&_.MuiButton-colorCallToAction]:rounded-xl!
          [&_.MuiButton-colorCallToAction]:bg-primary!

          [&_.MuiButton-colorCallToAction:hover]:bg-primary-variant!

          [&_.MuiButton-colorPrimary]:rounded-xl!
          [&_.MuiButton-colorPrimary]:bg-primary!

          [&_.MuiButton-colorPrimary:hover]:bg-primary-variant!

          [&_.MuiButton-outlined]:[outline:none]!
          [&_.MuiButton-outlined]:[border:none]!

          [&_.MuiButton-outlinedPrimary]:[outline:none]!
          [&_.MuiButton-outlinedPrimary]:[border:none]!

          [&_.MuiButton-outlinedSizeMedium]:[outline:none]!
          [&_.MuiButton-outlinedSizeMedium]:[border:none]!

          [&_.MuiLink-root]:text-primary!
          [&_.MuiLink-root]:[text-decoration-color:currentColor]!
          [&_.MuiLink-root]:[--Link-underlineColor:currentColor]!

          [&_.MuiPaper-root]:shadow-none! [&_.MuiPaper-root]:[box-shadow:none]!

          [&_.MuiTypography-body1]:[font-style:normal]!

          [&_.MuiTypography-body2-bold]:[text-transform:none]!

          [&_.MuiTypography-subtitle3-bold]:text-foreground-primary-variant

          [&_button_.MuiButton-icon]:text-on-primary!

          [&_button_.MuiButton-icon:hover]:text-on-primary!

          [&_button_.MuiTypography-body2-bold]:text-on-primary!

          [&_button_.MuiTypography-body2-bold:hover]:text-on-primary!

          [*+&]:mt-4

          md:[*+&]:mt-6
        "
        data-fs-element="plusgrade"
        id="pg-ew"
      />
    </>
  );
};
